import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Typography,
  Grid,
  Avatar,
  TextField,
  Chip,
  Pagination,
  Paper,
  Divider,
  Modal,
  useMediaQuery,
  CircularProgress,
} from "@mui/material";
import DOMPurify from "dompurify";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";

import { ViewJobAction } from "../../../../Redux/action/ViewJobAction";
import { VendorTeamTableAction } from "../../../../Redux/action/VendorTeamTableAction";
import { ApplyJobAction } from "../../../../Redux/action/ApplyJobAction";

import ebLogo from "../../../../images/ebench_logo.png";
import { FaCheck } from "react-icons/fa6";
import "react-toastify/dist/ReactToastify.css";
import { CandidateProfile } from "../../../../Redux/action/CandidateProfileAction";

const PRIMARY = "#286A94";
const ITEMS_PER_PAGE = 8;

export default function ViewJob() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const isMobile = useMediaQuery("(max-width:600px)");

  const { id } = useParams();
  const { userResult } = useSelector((state) => state.Auth);
  const paramsJobId = new URLSearchParams(location.search).get("jobId");
  const jobId = paramsJobId || id;

  const userInfo = useSelector((state) => state.ViewJob.result);
  const vendorCandidates = useSelector(
    (state) => state?.VendorTeamTableReducer?.result,
  );

  const applyLoading = useSelector((state) => state?.ApplyJob?.loading);

  const userType = userResult?.usertype;
  const vendorId = localStorage.getItem("vendorid");

  const appliedStatus = new URLSearchParams(location.search).get(
    "fromApplicationStatus",
  );
  const candidateData = useSelector((state) => state?.Profile?.result) || {};

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedCandidates, setSelectedCandidates] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  useEffect(() => {
    if (jobId) dispatch(ViewJobAction(jobId));
  }, [dispatch, jobId]);

  useEffect(() => {
    if (userType === "CANDIDATE" && userResult?.userid) {
      dispatch(CandidateProfile(userResult.userid));
    }

    if (userType === "VENDOR" && vendorId && jobId) {
      dispatch(VendorTeamTableAction(vendorId, jobId));
    }
  }, [dispatch, userType, vendorId, jobId, userResult?.userid]);

  /** ---------- FILTER + PAGINATION ---------- **/
  const filteredCandidates = vendorCandidates?.filter((c) =>
    `${c.firstName} ${c.lastName}`.toLowerCase().includes(search.toLowerCase()),
  );

  const selectableCandidates = filteredCandidates?.filter(
    (c) => c.profileCompleteness === "COMPLETE",
  );

  const pageCount = Math.ceil(filteredCandidates?.length / ITEMS_PER_PAGE);
  const paginatedCandidates = filteredCandidates?.slice(
    (page - 1) * ITEMS_PER_PAGE,
    page * ITEMS_PER_PAGE,
  );

  /** ---------- APPLY SUBMISSION ---------- **/
  const handleApplySubmit = () => {
    const data = {
      candidateId:
        userType === "CANDIDATE"
          ? [localStorage.getItem("userid")]
          : selectedCandidates,
      jobId,
    };

    dispatch(ApplyJobAction(data))
      .then((res) => {
        if (res?.data) {
          setModalOpen(false);

          setTimeout(() => {
            navigate(
              userType === "CANDIDATE" ? "/new_job" : "/all-new-jobs-list",
            );
          }, 1200);
        }
      })
      .catch(() => console.error("Something went wrong"));
  };

  /** ---------- CHECKBOX LOGIC ---------- **/
  const toggleCandidate = (candidateId) => {
    setSelectedCandidates((prev) =>
      prev.includes(candidateId)
        ? prev.filter((id) => id !== candidateId)
        : [...prev, candidateId],
    );
  };

  const toggleSelectAll = () => {
    const ids = selectableCandidates?.map((c) => c.candidateId);

    if (ids.every((id) => selectedCandidates.includes(id))) {
      setSelectedCandidates((prev) => prev.filter((id) => !ids.includes(id)));
    } else {
      setSelectedCandidates((prev) => [...new Set([...prev, ...ids])]);
    }
  };

  return (
    <>
      <Box sx={{ maxWidth: "1200px", mx: "auto", px: 2, py: 3 }}>
        <Typography
          variant="h5"
          fontWeight={700}
          sx={{
            color: "#286a94",
            letterSpacing: 0.5,
            lineHeight: 1.3,
            mb: 2,
          }}
        >
          Job Overview — ID: {userInfo?.jobUuid}
        </Typography>

        <Divider sx={{ my: 2 }} />

        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Paper
              sx={{
                p: 3,
                borderRadius: "16px",
                border: "1px solid #e5edf5",
                boxShadow: "0px 6px 20px rgba(0,0,0,0.05)",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "wrap",
                  whiteSpace: "nowrap",
                  gap: 2,
                  mb: 2,
                }}
              >
                <Typography
                  variant="h5"
                  sx={{ fontWeight: 700, color: PRIMARY, maxWidth: "70%" }}
                >
                  {userInfo?.jobTitle}
                </Typography>

                {!appliedStatus &&
                  userType !== "ADMIN" &&
                  (userType === "VENDOR" ||
                    (userType === "CANDIDATE" && !candidateData.vendorId)) && (
                    <Button
                      variant="contained"
                      onClick={() => {
                        if (
                          userType === "CANDIDATE" &&
                          !candidateData.vendorId
                        ) {
                          handleApplySubmit();
                        } else {
                          setModalOpen(true);
                        }
                      }}
                      sx={{
                        bgcolor: PRIMARY,
                        px: 4,
                        py: 1.3,
                        height: "42px",
                        borderRadius: "12px",
                        fontWeight: "600",
                        textTransform: "none",
                        whiteSpace: "nowrap",
                        "&:hover": {
                          bgcolor: "#1f587a",
                        },
                      }}
                    >
                      {applyLoading ? (
                        <CircularProgress size={22} sx={{ color: "#fff" }} />
                      ) : (
                        "Apply Now"
                      )}
                    </Button>
                  )}

                {appliedStatus && (
                  <Chip
                    icon={<FaCheck color="green" />}
                    label="Already Applied"
                    sx={{
                      bgcolor: "#e8ffee",
                      borderRadius: "10px",
                      fontWeight: 600,
                      px: 1.5,
                      color: "#167c2c",
                    }}
                  />
                )}
              </Box>

              <Divider sx={{ mb: 2 }} />

              {/* ---------- Job Information ---------- */}
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography sx={{ fontSize: "15px" }}>
                    <b>Work Type: </b>
                    <span style={{ color: "#4A5966" }}>
                      {userInfo?.workType}
                    </span>
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography sx={{ fontSize: "15px" }}>
                    <b>Job Type: </b>
                    <span style={{ color: "#4A5966" }}>
                      {userInfo?.employmentType === "FULL_TIME"
                        ? "Full Time"
                        : userInfo?.employmentType === "PART_TIME"
                          ? "Part Time"
                          : "Flexible"}
                    </span>
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography sx={{ fontSize: "15px" }}>
                    <b>Experience:</b>{" "}
                    <span style={{ color: "#4A5966" }}>
                      {(() => {
                        const min = Number(
                          String(userInfo?.minimumExperience || "").replace(
                            /\D/g,
                            "",
                          ),
                        );
                        const max = Number(
                          String(userInfo?.maximumExperience || "").replace(
                            /\D/g,
                            "",
                          ),
                        );
                        if (isNaN(min) || isNaN(max)) return "Not Disclosed";
                        if (min === 0 && max > 0)
                          return `Fresher to ${max} years`;
                        if (min === 0 && max === 0) return "Fresher";
                        if (min === max) return `${min} years`;
                        return `${min} to ${max} years`;
                      })()}
                    </span>
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography sx={{ fontSize: "15px" }}>
                    <b>Posted Date:</b>{" "}
                    <span style={{ color: "#4A5966" }}>
                      {userInfo?.postedDate || "N/A"}
                    </span>
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography sx={{ fontSize: "15px" }}>
                    <b>Location:</b>{" "}
                    <span style={{ color: "#4A5966" }}>
                      {userInfo?.workType || userInfo?.jobLocation || "N/A"}
                    </span>
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={12}>
                  <Typography sx={{ fontSize: "15px" }}>
                    <b>Timezone:</b>{" "}
                    <span style={{ color: "#4A5966" }}>
                      {userInfo?.timeZone}
                    </span>
                  </Typography>
                </Grid>
              </Grid>

              {/* ---------- Skills ---------- */}
              <Typography sx={{ mt: 3, fontWeight: 600, fontSize: "16px" }}>
                Required Skills
              </Typography>

              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
                {userInfo?.requiredSkills?.map((skill, i) => (
                  <Chip
                    key={i}
                    label={skill}
                    sx={{
                      bgcolor: "#eaf5ff",
                      color: PRIMARY,
                      fontWeight: 600,
                      borderRadius: "8px",
                    }}
                  />
                ))}
              </Box>

              {/* ---------- Description ---------- */}
              <Typography sx={{ mt: 3, fontWeight: 600, fontSize: "16px" }}>
                Job Description
              </Typography>

              <Typography
                sx={{
                  mt: 1,
                  whiteSpace: "pre-line",
                  lineHeight: 1.7,
                  color: "#4A5966",
                }}
              >
                <p
                  className="eBench-job-description mb-4 mt-4"
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(
                      userInfo?.jobDescription || "N/A",
                    ),
                  }}
                ></p>
              </Typography>
            </Paper>
          </Grid>

          {/* ---------- Right Sidebar ---------- */}
          <Grid item xs={12} md={4}>
            <Paper
              sx={{
                p: 3,
                textAlign: "center",
                borderRadius: "14px",
                boxShadow: "0px 5px 14px rgba(0,0,0,0.06)",
              }}
            >
              <Avatar
                src={ebLogo}
                imgProps={{
                  loading: "eager",
                  decoding: "async",
                }}
                sx={{
                  width: 200,
                  height: 80,
                  borderRadius: 0,
                  mx: "auto",
                  img: {
                    objectFit: "contain",
                  },
                }}
              />
              {/* <Typography sx={{ mt: 0.5, color:"#215b7f", fontWeight: 700, fontSize: "18px" }}>
                eBench Pvt. Ltd.
              </Typography> */}
              <Typography sx={{ fontSize: "14px", mt: 1, opacity: 0.7 }}>
                AI-powered talent acquisition & recruitment automation.
              </Typography>

              <Divider sx={{ my: 2 }} />

              <Typography sx={{ fontWeight: 600 }}>
                Why work with us?
              </Typography>
              <Box sx={{ mt: 1, color: "#45525e", lineHeight: 1.7 }}>
                • 🚀 Faster Hiring • 🎯 AI Skill Matching • 🌍 Remote-Friendly •
                🔐 Verified Employers
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Box>

      {/* ------------------ MODAL ------------------ */}

      <Modal open={modalOpen} onClose={() => setModalOpen(false)}>
        <Paper
          sx={{
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: isMobile ? "92%" : "600px",
            maxHeight: "90vh",
            overflow: "hidden",
            borderRadius: "18px",
          }}
        >
          {/* Header */}
          <Box
            sx={{
              p: 2.5,
              bgcolor: PRIMARY,
              color: "white",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6" fontWeight={700}>
              Select Candidates
            </Typography>
            <Button sx={{ color: "white" }} onClick={() => setModalOpen(false)}>
              ✕
            </Button>
          </Box>

          {/* Search */}
          <Box sx={{ p: 2 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search candidate..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              sx={{ borderRadius: "10px" }}
            />
          </Box>

          {/* Select All */}
          <Box sx={{ px: 2, py: 1, display: "flex", alignItems: "center" }}>
            <Checkbox
              checked={
                selectableCandidates?.length > 0 &&
                selectableCandidates.every((x) =>
                  selectedCandidates.includes(x.candidateId),
                )
              }
              onChange={toggleSelectAll}
            />
            <Typography>Select All</Typography>
          </Box>

          {/* Candidates List */}
          <Box sx={{ overflowY: "auto", maxHeight: "50vh", px: 2 }}>
            {filteredCandidates?.length > 0 ? (
              paginatedCandidates?.map((item) => (
                <Box
                  key={item.candidateId}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    p: 1.5,
                    borderBottom: "1px solid #f0f0f0",
                    cursor:
                      item.profileCompleteness === "COMPLETE"
                        ? "pointer"
                        : "not-allowed",
                    opacity: item.profileCompleteness !== "COMPLETE" ? 0.5 : 1,
                    "&:hover": {
                      background:
                        item.profileCompleteness === "COMPLETE"
                          ? "#eef8ff"
                          : "transparent",
                    },
                  }}
                  onClick={() => {
                    if (item.profileCompleteness === "COMPLETE") {
                      toggleCandidate(item.candidateId);
                    }
                  }}
                >
                  <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                    <Avatar
                      sx={{ bgcolor: PRIMARY }}
                      src={item.profileImageUrl}
                      imgProps={{
                        loading: "eager",
                        decoding: "async",
                      }}
                    >
                      {item.firstName?.charAt(0)?.toUpperCase()}
                    </Avatar>

                    <Box>
                      <Typography fontWeight={600}>
                        {item.firstName} {item.lastName}
                      </Typography>

                      <Chip
                        label={item.currentDesignation || "N/A"}
                        size="small"
                        sx={{
                          mt: 0.5,
                          bgcolor: "#e9f3ff",
                          color: PRIMARY,
                          borderRadius: "8px",
                          fontSize: "12px",
                          fontWeight: 500,
                        }}
                      />

                      <Chip
                        label={
                          item.profileCompleteness === "COMPLETE"
                            ? "Profile Complete"
                            : "Incomplete"
                        }
                        size="small"
                        sx={{
                          mt: 0.5,
                          ml: 1,
                          bgcolor:
                            item.profileCompleteness === "COMPLETE"
                              ? "#d9f7df"
                              : "#ffe4e4",
                          color:
                            item.profileCompleteness === "COMPLETE"
                              ? "#0f7a28"
                              : "#c91818",
                          fontSize: "12px",
                        }}
                      />
                    </Box>
                  </Box>

                  <Checkbox
                    disabled={item.profileCompleteness !== "COMPLETE"}
                    checked={selectedCandidates.includes(item.candidateId)}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleCandidate(item.candidateId);
                    }}
                  />
                </Box>
              ))
            ) : (
              <Typography
                sx={{ textAlign: "center", mt: 3, mb: 3, color: "#286a94" }}
              >
                No skill-matched candidates found
              </Typography>
            )}
          </Box>

          {/* Pagination */}
          {pageCount > 1 && (
            <Box sx={{ py: 2, textAlign: "center" }}>
              <Pagination
                count={pageCount}
                page={page}
                onChange={(e, val) => setPage(val)}
              />
            </Box>
          )}

          {/* Footer */}
          <Divider />
          <Box
            sx={{
              p: 2,
              display: "flex",
              justifyContent: "flex-end",
              gap: 2,
            }}
          >
            <Button
              variant="contained"
              sx={{
                bgcolor: "#286a94",
                px: 4,
                borderRadius: "10px",
                "&:hover": {
                  bgcolor: "#1f587a",
                },
              }}
              onClick={() => setModalOpen(false)}
            >
              Cancel
            </Button>

            <Button
              variant="contained"
              sx={{
                bgcolor: PRIMARY,
                px: 4,
                borderRadius: "10px",
                "&:hover": {
                  bgcolor: "#1f587a",
                },
              }}
              disabled={selectedCandidates.length === 0 || applyLoading}
              onClick={handleApplySubmit}
            >
              {applyLoading ? (
                <CircularProgress size={22} sx={{ color: "white" }} />
              ) : (
                "Submit"
              )}
            </Button>
          </Box>
        </Paper>
      </Modal>
    </>
  );
}
